import { getChatApiRoute } from '@renderer/chat/api/v1Client'

export interface PlannerDebugRun {
  id: string
  status: string
  details: string | null
  toolsets: Array<{ name: string; count: number }> | null
  createdAt: string
  updatedAt: string
  isReplay: boolean
  actionsEmitted: number
  remindersEmitted: number
  skillChatsSpawned: number
  scratchpadLikelyAvailable: boolean
  agentSpans: Array<{ stagedActionId: string; toolSet: string; toolName: string; permalink: string }>
}

export type PlannerScratchpad =
  | { status: 'available'; value: string }
  | { status: 'running' }
  | { status: 'errored'; error?: string }
  | { status: 'expired-or-missing' }

export interface PlannerDebugAction {
  id: string
  title?: string
  description?: string
  explanation?: string
  toolSet?: string
  toolName?: string
  inputData?: unknown
  outputData?: unknown
  createdAt: string
  resolvedAt?: string | null
  archivedAt?: string | null
  undoneAt?: string | null
  lastInvokedAt?: string | null
  braintrustSpanId?: string | null
}

export interface PlannerDebugReminder {
  id: string
  text: string
  description?: string | null
  type: string
  dueAt?: string | null
  priority?: boolean
  resolvedAt?: string | null
  createdAt: string
  source?: { title?: string } | null
}

export interface PlannerDebugSkillRun {
  conversationId: string
  createdAt: string
  skill: string | null
  toolset: string | null
  sourceIntegration: string | null
  state: 'running' | 'responded' | 'no-response'
  prompt: string | null
  promptTruncated: boolean
  firstResponse: string | null
  firstResponseTruncated: boolean
  stagedActionIds: Array<string>
}

export interface PlannerDebugRunDetail {
  run: PlannerDebugRun
  actions: Array<PlannerDebugAction>
  reminders: Array<PlannerDebugReminder>
  skillRuns: Array<PlannerDebugSkillRun>
  scratchpad: PlannerScratchpad
}

export interface PlannerDebugSummary {
  summary: string
  scratchpadStatus: PlannerScratchpad['status']
}

export interface PlannerReplaySourceReport {
  id: string
  disposition: 'recorded' | 'fresh' | 'omit'
  status?: 'found' | 'absent-at-run-time' | 'redacted' | 'schema-mismatch'
  contentBytes?: number
  error?: string
}

export interface PlannerReplayResult {
  preset: string
  dryRun: boolean
  launched: boolean
  jobId: string | null
  refusal: string | null
  noTrace: string | null
  trace: { rootSpanId: string; permalink: string } | null
  sources: Array<PlannerReplaySourceReport>
}

type GetAuthToken = () => Promise<string>

function errorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const value = body as { error?: unknown; message?: unknown }
    if (typeof value.error === 'string') return value.error
    if (typeof value.message === 'string') return value.message
    if (value.error && typeof value.error === 'object' && 'message' in value.error) {
      const nested = (value.error as { message?: unknown }).message
      if (typeof nested === 'string') return nested
    }
  }
  return `Planner debug request failed (${status}).`
}

async function requestJson<T>(
  noteId: string,
  path: string,
  getAuthToken: GetAuthToken,
  init?: { method: 'POST'; body?: unknown },
): Promise<T> {
  const token = await getAuthToken()
  const response = await fetch(getChatApiRoute(`api/v1/audio-notes/${encodeURIComponent(noteId)}/debug${path}`), {
    method: init?.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(init ? { body: JSON.stringify(init.body ?? {}) } : {}),
  })
  const body = (await response.json().catch(() => null)) as unknown
  if (!response.ok) {
    throw new Error(errorMessage(body, response.status))
  }
  return body as T
}

export async function getPlannerDebugAccess(getAuthToken: GetAuthToken): Promise<boolean> {
  const token = await getAuthToken()
  const response = await fetch(getChatApiRoute('api/v1/auth/me'), {
    headers: { Authorization: `Bearer ${token}` },
  })
  const body = (await response.json().catch(() => null)) as unknown
  if (!response.ok) {
    throw new Error(errorMessage(body, response.status))
  }
  if (!body || typeof body !== 'object' || typeof (body as { isAdmin?: unknown }).isAdmin !== 'boolean') {
    throw new Error('Planner debug access response was invalid.')
  }
  return (body as { isAdmin: boolean }).isAdmin
}

export async function listPlannerDebugRuns(
  noteId: string,
  getAuthToken: GetAuthToken,
): Promise<Array<PlannerDebugRun>> {
  const body = await requestJson<{ data: Array<PlannerDebugRun> }>(noteId, '/planner/runs', getAuthToken)
  return body.data
}

export async function getPlannerDebugRun(
  noteId: string,
  jobId: string,
  getAuthToken: GetAuthToken,
): Promise<PlannerDebugRunDetail> {
  const body = await requestJson<{ data: PlannerDebugRunDetail }>(
    noteId,
    `/planner/runs/${encodeURIComponent(jobId)}`,
    getAuthToken,
  )
  return body.data
}

export async function summarizePlannerDebugRun(
  noteId: string,
  jobId: string,
  getAuthToken: GetAuthToken,
): Promise<PlannerDebugSummary> {
  const body = await requestJson<{ data: PlannerDebugSummary }>(
    noteId,
    `/planner/runs/${encodeURIComponent(jobId)}/summarize`,
    getAuthToken,
    { method: 'POST' },
  )
  return body.data
}

export async function replayPlanner(
  noteId: string,
  dryRun: boolean,
  getAuthToken: GetAuthToken,
): Promise<PlannerReplayResult> {
  return requestJson<PlannerReplayResult>(noteId, '/planner/replay', getAuthToken, {
    method: 'POST',
    body: { dryRun },
  })
}
